import { CenterCard } from "../CenterCard";
import { PackageDateFlow } from "../DatePickerCard/PackageDateFlow";
import { BookingSlotsCard, BookingSlot } from "../BookingSlotsCard";
import { BookingHandoffCard } from "../BookingHandoffCard";
import styles from "./ToolResponseRenderer.module.css";

export interface ToolResultData {
    tool_name: string;
    data: Record<string, any>;
}

interface ToolResponseRendererProps {
    toolResults: ToolResultData[];
    onCardSelect?: (text: string) => void;
}

/** Check if tool results contain card-renderable booking data. */
export const hasCardResults = (toolResults: ToolResultData[]): boolean => {
    if (!toolResults || toolResults.length === 0) return false;
    for (const { tool_name, data } of toolResults) {
        if (tool_name === "booking_agent" && data?.result) {
            const intent = data.intent as string;
            const result = data.result as Record<string, any>;
            if (intent === "branches" && result.centers) return true;
            if (intent === "packages" && result.packages) return true;
            if (intent === "availability" && result.available_slots) return true;
            if (intent === "prepare_handoff" && result.status === "ready" && result.portal_url) return true;
        }
    }
    return false;
};

/** Plan A: cards always replace the LLM text — there is no booking_list flow that needs paired text. */
export const shouldHideTextForCards = (toolResults: ToolResultData[]): boolean => {
    return hasCardResults(toolResults);
};

export const ToolResponseRenderer = ({
    toolResults,
    onCardSelect,
}: ToolResponseRendererProps) => {
    if (!toolResults || toolResults.length === 0) return null;

    const elements: JSX.Element[] = [];

    for (const { tool_name, data } of toolResults) {
        // booking_agent responses carry { intent, result }
        if (tool_name === "booking_agent" && data?.result) {
            const intent = data.intent as string;
            const result = data.result as Record<string, any>;

            if (intent === "branches" && result.centers) {
                elements.push(
                    <div key="centers" className={styles.container}>
                        <CenterCard
                            centers={result.centers}
                            onSelect={(name) =>
                                onCardSelect?.(`I'd like to go to ${name}`)
                            }
                        />
                    </div>,
                );
            }

            if (intent === "packages" && result.packages) {
                elements.push(
                    <div key="packages" className={styles.container}>
                        <PackageDateFlow
                            center={result.center ?? ""}
                            packages={result.packages}
                            onComplete={(text) => onCardSelect?.(text)}
                        />
                    </div>,
                );
            }

            if (intent === "availability" && result.available_slots) {
                elements.push(
                    <div key="slots" className={styles.container}>
                        <BookingSlotsCard
                            packageName={result.package ?? ""}
                            slots={result.available_slots as BookingSlot[]}
                            onSelectSlot={(slot) =>
                                onCardSelect?.(
                                    `I'd like the ${slot.time} slot on ${slot.date}`,
                                )
                            }
                        />
                    </div>,
                );
            }

            if (
                intent === "prepare_handoff" &&
                result.status === "ready" &&
                result.portal_url &&
                result.booking_summary
            ) {
                elements.push(
                    <div key="handoff" className={styles.container}>
                        <BookingHandoffCard
                            summary={result.booking_summary}
                            portalUrl={result.portal_url}
                        />
                    </div>,
                );
            }
        }
    }

    if (elements.length === 0) return null;

    return <>{elements}</>;
};
